import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, BarChart, Check, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../services/api";

export default function ProgramDetail() {
  const { slug } = useParams();

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await api.get(`/programs/${slug}`);
        const data = response?.data || {};

        setProgram({
          slug: data?.slug || "",
          name: data?.name || "",
          description: data?.description || "",
          short_description: data?.short_description || "",
          image: data?.image || "",
          category: data?.category || "",
          duration: data?.duration || "",
          price: data?.price || "0",
          level: data?.level || "",

          // keep UI safe (empty arrays so sections don't crash)
          features: [],
          weekly_structure: [],
          results: [],
        });
      } catch (error) {
        console.error("Error fetching program:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--dvx-bg)] flex items-center justify-center">
        <div className="font-oswald text-2xl text-[var(--dvx-text)] uppercase animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl mb-4">Program Not Found</h1>
          <Link to="/programs">
            <Button>Back to Programs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* HERO */}

      <section className="relative py-16">
        <div className="absolute inset-0">
          <img
            src={program.image || ""}
            alt={program.name || "program"}
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <Link to="/programs" className="flex items-center mb-8">
            <ArrowLeft size={16} className="mr-2" />
            Back to Programs
          </Link>

          <div className="flex gap-4 mb-4">
            {program.category && (
              <span className="bg-orange-500 text-white px-3 py-1 text-xs">
                {program.category}
              </span>
            )}

            {program.level && (
              <span className="flex items-center gap-1 text-sm">
                <BarChart size={14} />
                {program.level}
              </span>
            )}

            {program.duration && (
              <span className="flex items-center gap-1 text-sm">
                <Clock size={14} />
                {program.duration}
              </span>
            )}
          </div>

          <h1 className="text-5xl font-bold">{program.name}</h1>

          {program.short_description && (
            <p className="mt-4 max-w-3xl">{program.short_description}</p>
          )}

          <div className="mt-8 flex gap-6 items-center">
            <span className="text-4xl font-bold text-orange-500">
              ${program.price}
            </span>

            <Link to="/pricing">
              <Button>Enroll Now</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CONTENT */}

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}

            <div>
              <h2 className="text-2xl font-bold mb-4">Program Overview</h2>

              <div
                className="text-[var(--dvx-text-muted)] leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: program.description || "",
                }}
              />
            </div>

            {/* FEATURES */}
            {program.features?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">What's Included</h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  {program.features.map((feature, i) => (
                    <div key={i} className="flex gap-3 border p-4">
                      <Check size={18} className="text-orange-500" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WEEKLY STRUCTURE */}
            {program.weekly_structure?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Weekly Structure</h2>

                {program.weekly_structure.map((day, i) => (
                  <div key={i} className="flex justify-between border p-4 mb-2">
                    <span className="flex gap-2 items-center">
                      <Calendar size={16} />
                      {day?.day || ""}
                    </span>

                    <span>{day?.focus || ""}</span>
                  </div>
                ))}
              </div>
            )}

            {/* RESULTS */}
            {program.results?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Expected Results</h2>

                {program.results.map((result, i) => (
                  <div key={i} className="flex gap-3 mb-3">
                    <Check size={16} className="text-orange-500" />
                    <span>{result}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SIDEBAR */}

          <div className="border p-8 h-fit sticky top-32">
            <h3 className="text-xl font-bold mb-6">Program Summary</h3>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between">
                <span>Duration</span>
                <span>{program.duration || "-"}</span>
              </div>

              <div className="flex justify-between">
                <span>Level</span>
                <span>{program.level || "-"}</span>
              </div>

              <div className="flex justify-between">
                <span>Category</span>
                <span>{program.category || "-"}</span>
              </div>

              <div className="flex justify-between">
                <span>Price</span>
                <span className="text-orange-500 font-bold">
                  ${program.price || "0"}
                </span>
              </div>
            </div>

            <Link to="/pricing">
              <Button className="w-full">Enroll Now</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
